import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { listElementHoverCss } from '@ui/hoverStates.ts';
import { CircleIcon } from '@ui/icons/CircleIcon.tsx';
import ScrollableDropShadow from '@ui/ScrollableDropShadow.tsx';

import { handleKeyboardSelect } from '../../../utils/keyboardUtils.ts';

const PoiCategories = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  justify-content: flex-start;
  max-height: 100%;
  width: auto;
  flex-wrap: wrap;

  &::after {
    content: '';
    flex: auto;
  }
`;

const PoiCategoriesLi = styled.li`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex: none;
  ${({ theme }) => theme.fontSize('14px')};
  min-height: 52px;
  position: relative;
  width: 100%;
  &:hover,
  &:active,
  .selected {
    ${listElementHoverCss};
  }

  &.mobile {
    min-height: 72px;
    flex-direction: column;
    position: relative;
    justify-content: center;

    .category {
      display: flex;
      align-items: center;
      width: 100%;
      flex-wrap: wrap;
    }
  }

  .category {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    ${props => (props.$isMobile ? 'margin: 0 24px; padding: 0 0 0 14px; ' : 'margin: 0 16px 0 20px; padding: 0;')}
  }

  .divider {
    width: calc(100% - 32px);
    height: 1px;
    background: #ebebeb;
    position: absolute;
    bottom: 0;
  }
`;

const PoiCategoriesButton = styled.button`
  cursor: pointer;
  width: 100%;
  height: 100%;
  background: transparent;
  border: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  &:focus-visible {
    outline-offset: -3px;
    border-radius: 4px;
  }
`;

const PoiCategoryTitle = styled.span`
  display: block;
  user-select: none;
  color: var(--color-primaryText);
  font-size: 16px;
  line-height: 150%;
  margin-inline-start: 14px;
  text-align: start;
  word-break: break-word;
  overflow-wrap: break-word;
  flex: 1;
`;

export const CategoryListWrapper = styled.div`
  padding: ${props => (props.$isMobile ? '0' : '8px 0')};
  min-height: 10em;
`;

export const SearchResultsWrapper = styled.div`
  padding-bottom: ${props => (props.$hasPadding ? '40px' : '0')};

  .-mobile & {
    height: auto;
    min-height: 100%;
  }
`;

export const CategoryList = ({ isMobile, poiCategories, handlePoiCategoryButtonClick, T }) => {
  return (
    <PoiCategories
      data-testid="poiCategoriesList"
      className={isMobile ? 'mobile' : null}
      aria-label={T('homeView:Navigation Items')}
    >
      {poiCategories &&
        poiCategories.length &&
        poiCategories.map(poiCategory => (
          <PoiCategoriesLi key={poiCategory.name}>
            <PoiCategoriesButton
              className={isMobile ? 'mobile' : null}
              onClick={() => handlePoiCategoryButtonClick(poiCategory)}
              data-testid="searchCategory"
              onKeyDown={handleKeyboardSelect(() => handlePoiCategoryButtonClick(poiCategory))}
              tabIndex={0}
            >
              <div className="category">
                <CircleIcon
                  name={poiCategory.svgId || poiCategory.category}
                  cdn
                  backgroundColor={poiCategory.backgroundColor}
                  size={isMobile ? 30 : 40}
                />
                <PoiCategoryTitle>{poiCategory.name}</PoiCategoryTitle>
              </div>
              <div className={isMobile ? 'divider' : null} />
            </PoiCategoriesButton>
          </PoiCategoriesLi>
        ))}
    </PoiCategories>
  );
};

const CategoryListView = ({ widgetState, isMobile, onPoiCategoryButtonClick, T }) => {
  const [state, setWidgetState] = useState(widgetState.getState());
  useEffect(() => widgetState.addCallback(setWidgetState), [widgetState]);

  return (
    <ScrollableDropShadow T={T} isDesktop={!isMobile}>
      <CategoryListWrapper $isMobile={isMobile}>
        <CategoryList
          isMobile={isMobile}
          poiCategories={state.poiCategories}
          handlePoiCategoryButtonClick={onPoiCategoryButtonClick}
          T={T}
        />
      </CategoryListWrapper>
    </ScrollableDropShadow>
  );
};

CategoryListView.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  onPoiCategoryButtonClick: PropTypes.func.isRequired,
};

export default CategoryListView;
